import type { BoxProps } from '@mui/material'
import { Box, Tab, Tabs, Typography } from '@mui/material'
import type { Chord } from '@tunasong/music-lib'
import { ChordLib, GuitarLib, ScaleLib } from '@tunasong/music-lib'
import type { NoteName, Scale } from '@tunasong/schemas'
import { HBox } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { FindGuitarChord } from '../guitar/find-chord/index.js'
import { Note } from '../note/index.js'
import ChordLibrary from './chord-library.js'

export interface ChordChooserProps extends Omit<BoxProps, 'onChange'> {
  chord?: Chord
  scale: Scale | null

  onChange(chord: Chord): void
}

export const ChordChooser: FC<ChordChooserProps> = props => {
  const { chord, scale, onChange, ...boxProps } = props

  const [currentNotes, setCurrentNotes] = useState<NoteName[]>(chord ? ChordLib.getNotes(chord) : [])

  const handleGuitarChord = (chord: GuitarLib.GuitarChord) => {
    /** onChange is a partial update, so we need to clear root and variant.   */
    onChange(chord)

    setCurrentNotes(ChordLib.getNotes(chord))
  }

  // const handleChangeRoot = (ev: SelectChangeEvent) => setRoot(ev.target.value)
  type TabValue = 'inscale' | 'all' | 'fretboard' | 'suggestions'
  const initialTab = scale ? 'inscale' : 'all'
  const [tab, setTab] = useState<TabValue>(initialTab)
  const handleTab = useCallback((ev: unknown, value: TabValue) => {
    setTab(value)
    setCurrentNotes([])
  }, [])
  const scaleName = scale ? ScaleLib.getName(scale) : 'No scale selected'

  useEffect(() => {
    if (!scale && tab === 'inscale') {
      setTab('all')
    }
  }, [scale, tab])

  return (
    <Box {...boxProps}>
      <HBox sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        {currentNotes ? (
          <Box>
            {chord ? (
              <Typography variant="body2" component="span" sx={{ mr: 1 }}>
                Notes in {ChordLib.getName(chord)}:
              </Typography>
            ) : null}
            {currentNotes.map((n, idx) => (
              <Note sx={{ m: 1 }} key={idx} note={n} scale={scale} />
            ))}
          </Box>
        ) : (
          <Typography variant="body2">N/A</Typography>
        )}
      </HBox>
      <Tabs sx={{ mb: 2 }} value={tab} onChange={handleTab}>
        <Tab label={scaleName} value={'inscale'} />
        <Tab label={`All Chords`} value={'all'} />
        <Tab label={`Fretboard`} value={'fretboard'} />
      </Tabs>
      {tab === 'inscale' ? (
        <ChordLibrary
          selected={chord}
          scale={scale}
          chords={ScaleLib.getAllChords(scale)}
          onChange={handleGuitarChord}
        />
      ) : tab === 'all' ? (
        <ChordLibrary selected={chord} scale={scale} onChange={handleGuitarChord} />
      ) : tab === 'fretboard' ? (
        <FindGuitarChord
          sx={{ minWidth: 500 }}
          defaultChord={GuitarLib.isGuitarChord(chord) ? chord : undefined}
          scale={scale}
          onSelected={handleGuitarChord}
        />
      ) : tab === 'suggestions' ? (
        'Suggestions are coming soon!'
      ) : null}
    </Box>
  )
}

export default ChordChooser
